import React, { useState } from "react";
import {
  Card,
  Typography,
  Divider,
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Pedido } from "../pages/Historico/Historico.tsx";
import { useAuth } from "../context/AuthContext.tsx";
import { obtendo_preco_produto_atual_faturado } from '../services/prods-api.js';
import { useCart } from "../context/CartContext.tsx";
import OrderInvoicedDetailsAlert from "./OrderInvoicedDetailsAlert.tsx";
import RepeatIcon from '@mui/icons-material/Repeat';
import InfoIcon from '@mui/icons-material/Info';
import CartDrawer from './CartDrawer.tsx';

interface CardOrderProps {
  pedido: Pedido;
}

const CardOrderInvoiced = ({ pedido }: CardOrderProps) => {
  const [openDetails, setOpenDetails] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openCart, setOpenCart] = React.useState(false);

  const { userData } = useAuth();
  const { addToCart, clearCart } = useCart(); 

  const itensVisiveis = pedido.itens.slice(0, 2);
  const maisItens = pedido.itens.length - itensVisiveis.length;

  const formatarData = (data: string) => {
    const dataHora = new Date(`${data}`);
    return dataHora.toLocaleString("pt-BR", {
      dateStyle: "short",
    });
  };

  const calcularTotalPedido = () => {
    return pedido.itens.reduce((total, item) => total + item.itenS_QUANTIDADE * item.itenS_PRECO_BRUTO, 0);
  };

  const handleOpenDetails = () => {
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const pedirNovamente = async (lista_itens) => {
    setOpenBackdrop(true);
    const cnpj_cpf_usuario = userData.clI_CNPJCPF;
   
    try {
      clearCart();
      var response = await obtendo_preco_produto_atual_faturado(lista_itens, cnpj_cpf_usuario);
  
      if(response.data.sucesso){
        var lista_prods = response.data.lista_produtos_atualizados;
  
        lista_prods.forEach(produto => {
          addToCart(produto);
        });

        setOpenBackdrop(false);
        setOpenCart(true);
        
      } else {
        setOpenBackdrop(false);
        throw new Error('Falha ao obter valores atualizados dos produtos');
      }

    }catch (error) {
      setOpenBackdrop(false);
      console.error(error);
    }
  };

  const handleCloseBackdrop = () => {
    setOpenBackdrop(false);
  };

  const toggleDrawer = (newOpenCart : boolean ) => {
    setOpenCart(newOpenCart);
  };  

  return (
    <>
      <Card
        key={pedido.capa.notA_NUMERO}
        sx={{
          width: "100%",
          boxShadow: 3,
          transition: "transform 0.3s",
          marginTop: 3,
          "&:hover": {
            transform: "scale(1.02)",
          },
          p: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: "#357a38",
            color: "#fff",
            px: 1,
            py: 0.5,
            fontSize: 12,
            fontWeight: "bold",
            width: 65,
          }}
        >
          Faturado
        </Box>
        <Box display="flex" 
          justifyContent="space-between"
          flexDirection={{ xs: "column", sm: "row" }} 
          alignItems={{ xs: "flex-start", sm: "center" }}
          mt={2}
          gap={1}>
          <Typography variant="body2" color="#9e9e9e" mt={2}>
            {formatarData(pedido.capa.notA_DT_EMISSAO)} | Nota Nº : {pedido.capa.notA_NUMERO} | Serie : {pedido.capa.notA_SERIE}
          </Typography>

          <Typography variant="subtitle1" py={1} fontWeight="bold">
            Total : R$ {calcularTotalPedido().toFixed(2)}
          </Typography>
        </Box>
        <Divider sx={{ my: 1}} />

        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold">
              Pedido {formatarData(pedido.capa.data, pedido.capa.hora)}
            </Typography>

            <Box mt={1}>
              {itensVisiveis.map((item, index) => (
                <Typography variant="body2" color="text.secondary" key={index}>
                  {item.proD_REF_FORN} • {item.qtd}x {item.proD_DESCRICAO}
                </Typography>
              ))}
              {maisItens > 0 && (
                <Typography variant="body2" color="text.secondary">
                  • ...e mais {maisItens} item(ns)
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="flex-end"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1}>
            <Button variant="contained" color="primary" sx={{ "&:hover": { backgroundColor: "#357a38" }}} onClick={() => pedirNovamente(pedido.itens)}  startIcon={<RepeatIcon />}>
              <Typography fontWeight="bold">Pedir novamente</Typography>
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenDetails} startIcon={<InfoIcon />}>
              <Typography fontWeight="bold">Detalhes do pedido</Typography>
            </Button>
          </Box>

        </Box>
      </Card>

      <OrderInvoicedDetailsAlert open={openDetails} onClose={handleCloseDetails} pedido={pedido} />

      <Backdrop
        open={openBackdrop}
        sx={(theme) => ({
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1,
          flexDirection: 'column',
        })}
        onClick={handleCloseBackdrop}
      >
        <Stack
          direction="column"
          spacing={2}
          alignItems="center"

        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" fontWeight="bold" textAlign="center">
            OBTENDO VALORES ATUALIZADOS DOS PRODUTOS...
          </Typography>
        </Stack>
      </Backdrop>

      <CartDrawer open={openCart} onClose={() => toggleDrawer(false)} />
    </>
  );
};

export default CardOrderInvoiced;
